import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Typography, DatePicker, Spin } from "antd";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { fetchEmployeePerformanceAsync } from "../../../Slices/employeePerformanceSlice";
import { SelectInput } from "../../../blueplanitUtils/InputField";

const { Title, Text } = Typography;

const formatHours = (hours) => {
    if (!hours || isNaN(hours)) return "0 sec";

    const totalSeconds = hours * 3600;
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);

    if (hrs >= 1) {
        return `${hrs} hr${hrs > 1 ? "s" : ""} ${mins > 0 ? mins + " min " : ""}${secs > 0 ? secs + " sec" : ""}`.trim();
    }

    if (mins >= 1) {
        return `${mins} min${mins > 1 ? "s" : ""} ${secs > 0 ? secs + " sec" : ""}`.trim();
    }

    return `${secs} sec`;
};

const checkDueStatus = (dueArr = [], compArr = []) => {
    if (!Array.isArray(dueArr) || !Array.isArray(compArr)) return "Pending";

    const hasAnyCompleted = compArr.some(c => c !== null && c !== undefined && c !== "");
    if (!hasAnyCompleted) return "Pending";

    let anyFailed = false;
    let anySuccess = false;

    const len = Math.max(dueArr.length, compArr.length);
    for (let i = 0; i < len; i++) {
        const due = dueArr[i];
        const comp = compArr[i];
        if (due == null || comp == null) continue;
        const dueNum = Number(due);
        const compNum = Number(comp);
        if (isNaN(dueNum) || isNaN(compNum)) continue;
        if (compNum > dueNum) anyFailed = true;
        else anySuccess = true;
    }

    if (anyFailed) return "Failed";
    if (anySuccess) return "Success";
    return "Pending";
};

const getSLACountByClient = (dueArr = [], compArr = []) => {
    let success = 0, failed = 0, pending = 0;

    for (let i = 0; i < Math.max(dueArr.length, compArr.length); i++) {
        const due = Number(dueArr[i]);
        const comp = Number(compArr[i]);

        if (!due || !comp) {
            pending++;
            continue;
        }

        if (comp > due) failed++;
        else success++;
    }

    return { success, failed, pending };
};

const EmployeePerformanceEmployeeView = () => {
    const dispatch = useDispatch();
    const { performanceData = [], loading, error } = useSelector(
        state => state.employeePerformance || {}
    );
    const allEmployees = useSelector(state => state.employee?.AllEmployees || []);

    const today = dayjs();
    const [dateRange, setDateRange] = useState([today.startOf("day"), today.endOf("day")]);
    const [fromDate, toDate] = dateRange;
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [expandedKey, setExpandedKey] = useState(null);
    const ExpandedRowContent = ({ record }) => {
        const { success, failed, pending } = getSLACountByClient(
            record.dueDate,
            record.completedDate
        );

        return (
            <Card style={{ margin: "10px 0", borderRadius: 10, background: "#fafafa" }}>
                <Title level={5}>Non-Productive Task Breakdown</Title>
                {(!record.nonProductiveTasks || record.nonProductiveTasks.length === 0) ? (
                    <p>No Non-Productive Tasks</p>
                ) : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: "#f0f0f0" }}>
                                <th style={{ padding: 8 }}>Task</th>
                                <th style={{ padding: 8 }}>Hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {record.nonProductiveTasks.map((t, idx) => (
                                <tr key={idx}>
                                    <td style={{ padding: 8 }}>{t.task}</td>
                                    <td style={{ padding: 8 }}>{formatHours(t.hours)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <Title level={5} style={{ marginTop: 20 }}>SLA Details</Title>

                <div style={{ display: "flex", gap: 20 }}>
                    <span><strong>SLA Success:</strong> {success}</span>
                    <span><strong>SLA Failed:</strong> {failed}</span>
                    <span><strong>SLA Pending:</strong> {pending}</span>
                </div>
            </Card>
        );
    };

    // Build list of operation employees for selector
    const OperationEmployees = useMemo(() => {
        return allEmployees
            ?.filter(emp => {
                const dep = (emp?.department || "").toLowerCase();
                return dep === "operations" || dep === "operation";
            })
            ?.map(emp => ({
                label: `${emp.firstname || ""} ${emp.lastname || ""}`.trim(),
                value: emp.id,
                dbEmp: emp
            })) || [];
    }, [allEmployees]);

    useEffect(() => {
        if (!selectedEmployee && OperationEmployees.length > 0) {
            setSelectedEmployee(OperationEmployees[0].value);
        }
    }, [OperationEmployees]);

    useEffect(() => {
        if (selectedEmployee && fromDate && toDate) {
            dispatch(
                fetchEmployeePerformanceAsync({
                    employeeId: selectedEmployee,
                    startDate: fromDate.toISOString(),
                    endDate: toDate.toISOString()
                })
            );
        }
    }, [selectedEmployee, fromDate, toDate, dispatch]);

    const selectedEmployeeData = useMemo(() => {
        return allEmployees.find(emp => emp.id === selectedEmployee) || null;
    }, [allEmployees, selectedEmployee]);

    const employeeClients = useMemo(() => {
        return (
            selectedEmployeeData?.client?.map(c => (c?.NameOfClient || "").trim()).filter(Boolean) || []
        );
    }, [selectedEmployeeData]);

    // backend records
    const backendRecords = performanceData || [];
    const allClients = useMemo(() => {
        const assigned = employeeClients.map(c => c.trim());
        const fromBackend = backendRecords.map(r => (r.clientName || "").trim()).filter(Boolean);
        return Array.from(new Set([...assigned, ...fromBackend]));
    }, [employeeClients, backendRecords]);

    const clientRows = useMemo(() => {
        return allClients.map((clientName, idx) => {
            const rec = backendRecords.find(
                r => (r.clientName || "").trim().toLowerCase() === clientName.trim().toLowerCase()
            );

            const productive = rec?.productive || {};

            return {
                key: idx,
                clientName,
                totalHours: Number(rec?.totalHours || 0),
                emails: Number(productive?.emails || 0),
                processing: Number(productive?.processing || 0),
                totalProductiveHours: Number(productive?.totalProductiveHours || 0),
                nonProductiveHours: Number(productive?.nonProductiveHours || 0),
                totalRequests: Number(rec?.totalRequests || 0),
                dueDate: rec?.dueDate || [],
                completedDate: rec?.completedDate || [],
                SLA: rec ? checkDueStatus(rec.dueDate || [], rec.completedDate || []) : "-",
                nonProductiveTasks: rec?.productive?.nonProductiveTasks || [],
            };
        });
    }, [allClients, backendRecords]);

    const totalHours = clientRows.reduce((s, c) => s + (c.totalHours || 0), 0);
    const productiveTotal = clientRows.reduce((s, c) => s + (c.totalProductiveHours || 0), 0);
    const nonProductiveTotal = clientRows.reduce((s, c) => s + (c.nonProductiveHours || 0), 0);
    const slaSummary = {
        Success: clientRows.filter(c => c.SLA === "Success").length,
        Failed: clientRows.filter(c => c.SLA === "Failed").length,
        Pending: clientRows.filter(c => c.SLA === "Pending").length
    };

    const chartOption = useMemo(() => ({
        title: { text: "Hours by Client", left: "center" },

        tooltip: {
            trigger: "axis",
            formatter: (params) => {
                const clientName = params[0].axisValue;
                const row = clientRows.find(c => c.clientName === clientName);

                let html = `<strong>${clientName}</strong><br/>`;

                params.forEach(p => {
                    html += `
            <span style="display:inline-block;margin-right:6px;
                border-radius:50%;width:10px;height:10px;background:${p.color}"></span>
            ${p.seriesName}: <strong>${formatHours(Number(p.data) || 0)}</strong><br/>
        `;

                    if (p.seriesName === "Non-Productive" && row?.nonProductiveTasks?.length > 0) {
                        html += `<div style="margin-left:14px; margin-top:4px;"><u>Breakdown:</u><br/>`;
                        row.nonProductiveTasks.forEach(t => {
                            html += `${t.task}: ${formatHours(Number(t.hours) || 0)}<br/>`;
                        });
                        html += `</div>`;
                    }
                });

                return html;
            }

        },
        legend: { top: 30, data: ["Emails", "Processing", "Non-Productive"] },
        xAxis: {
            type: "category",
            data: clientRows.map(c => c.clientName),
            axisLabel: { rotate: 20 }
        },
        yAxis: { type: "value", name: "Hours" },
        series: [
            { name: "Emails", type: "bar", stack: "hours", data: clientRows.map(c => c.emails) },
            { name: "Processing", type: "bar", stack: "hours", data: clientRows.map(c => c.processing) },
            { name: "Non-Productive", type: "bar", stack: "hours", data: clientRows.map(c => c.nonProductiveHours), itemStyle: { color: "#d64a4cff" } }
        ]
    }), [clientRows]);

    const columns = [
        { title: "Client", dataIndex: "clientName" },
        {
            title: "Total Hours",
            dataIndex: "totalHours",
            render: hrs => formatHours(Number(hrs) || 0)

        },
        {
            title: "Emails",
            dataIndex: "emails",
            render: hrs => formatHours(Number(hrs) || 0)

        },
        {
            title: "Processing",
            dataIndex: "processing",
            render: hrs => formatHours(Number(hrs) || 0)

        },
        {
            title: "Productive Hours",
            dataIndex: "totalProductiveHours",
            render: hrs => formatHours(Number(hrs) || 0)

        },
        {
            title: "Non-Productive Hours",
            dataIndex: "nonProductiveHours",
            render: hrs => formatHours(Number(hrs) || 0)

        },
        { title: "Number of Time Records", dataIndex: "totalRequests" },
        {
            title: "SLA Count",
            dataIndex: "slaCount",
            render: (_, record) => {
                const { success, failed, pending } = getSLACountByClient(
                    record.dueDate || [],
                    record.completedDate || []
                );

                const total = success + failed + pending;
                return total;
            }
        }
    ];

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <Row justify="space-between" align="middle" style={{ marginBottom: 30 }}>
                    <Col>
                        <Title level={3}>Employee Performance - Employee View</Title>
                        <Text type="secondary">Select an employee to view performance</Text>
                    </Col>

                    <Col style={{ display: "flex", gap: 15 }}>
                        <SelectInput
                            style={{ width: 220 }}
                            placeholder="Select Employee"
                            value={selectedEmployee}
                            onChange={setSelectedEmployee}
                            options={OperationEmployees}
                            allowClear
                            showSearch
                        />

                        <DatePicker
                            style={{ width: 120, height: 32 }}
                            value={fromDate}
                            onChange={(date) => setDateRange([date.startOf("day"), toDate])}
                            format="DD/MM/YYYY"
                            placeholder="From Date"
                        />

                        <DatePicker
                            style={{ width: 120, height: 32 }}
                            value={toDate}
                            onChange={(date) => setDateRange([fromDate, date.endOf("day")])}
                            format="DD/MM/YYYY"
                            placeholder="To Date"
                        />
                    </Col>
                </Row>

                {error && <p style={{ color: "red", textAlign: "center" }}>Error: {String(error)}</p>}

                {loading && (
                    <div style={{ textAlign: "center", padding: 60 }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 12 }}>Loading performance data...</p>
                    </div>
                )}

                {!selectedEmployee && !loading && (
                    <p style={{ textAlign: "center", marginTop: 50, fontSize: 16 }}>
                        Please select an <strong>Employee</strong>.
                    </p>
                )}

                {selectedEmployee && !loading && (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card
                                title={<div style={{ textAlign: "center" }}><Title level={4}>{OperationEmployees.find(e => e.value === selectedEmployee)?.label || "-"}</Title></div>}
                                style={{ borderRadius: 12, minHeight: 480 }}
                            >
                                <ReactECharts option={chartOption} style={{ height: 300 }} />

                                <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                                    <p><strong>Total Hours:</strong> {formatHours(totalHours)}</p>
                                    <p><strong>Productive:</strong> {formatHours(productiveTotal)}</p>
                                    <p><strong>Non-Productive:</strong> {formatHours(nonProductiveTotal)}</p>
                                    <p><strong>SLA Success:</strong> {slaSummary.Success}</p>
                                    <p><strong>SLA Failed:</strong> {slaSummary.Failed}</p>
                                    <p><strong>SLA Pending:</strong> {slaSummary.Pending}</p>
                                </div>

                                <div style={{ marginTop: 20 }}>
                                    <CustomGridTable
                                        columns={columns}
                                        Id="employee-view-table"
                                        dataSource={clientRows}
                                        pagination={false}
                                        expandable={{
                                            expandedRowKeys: expandedKey !== null ? [expandedKey] : [],
                                            expandedRowRender: (record) => <ExpandedRowContent record={record} />,
                                            rowExpandable: () => true,
                                            showExpandColumn: false
                                        }}
                                        onRow={(record) => ({
                                            onClick: () => setExpandedKey(expandedKey === record.key ? null : record.key)
                                        })}
                                    />
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </div>
    );
};

export default EmployeePerformanceEmployeeView;
